import { HCAxiosInstance } from 'core/utils/hcAxios'
import { TLanguages } from 'core/config/locale'
import {
  Client,
  handleDefaultClientQueries,
  handleDefaultServerQueries,
  Platform,
} from 'core/hooks/InitApp/handleDefaultQueries'
import handleDecryptData from 'core/utils/handleDecryptData'
import handleSignature from 'core/utils/handleSignature'
import { dispatchLogout } from 'core/components/Authenticate'
import { localCache } from '@hotcoin/utils/dist/utils/cache'
import { baseURLClient, baseURLServer, baseURLServerReferer } from '@/config'
import useAxiosResponseErrorHandler, { ErrorNotifier } from 'core/hooks/InitApp/useAxiosResponseErrorHandler'
import getDeviceId from 'core/utils/getDeviceId'

/** 请求拦截器ID存储 */
const interceptorsRequestIds: number[] = []
/** 响应拦截器ID存储 */
const interceptorsResponseIds: number[] = []

/** axios初始化配置的参数接口 */
interface AxiosInitializerProps {
  /** 错误通知处理器 */
  errorNotifier: ErrorNotifier
  /** 平台信息 */
  platform: Platform
  /** 客户端信息 */
  client: Client
  /** axios实例 */
  hcAxios: HCAxiosInstance
  /** 语言设置 */
  lang: TLanguages
}

/**
 * Axios 初始化配置 Hook
 * @description 配置 axios 实例的拦截器、baseURL 等基础设置
 */
const useAxiosInitializer = ({ errorNotifier, platform, client, hcAxios, lang }: AxiosInitializerProps) => {
  // 获取错误处理器
  const axiosResponseErrorHandler = useAxiosResponseErrorHandler(lang, errorNotifier)

  /**
   * 清除现有拦截器
   */
  const clearInterceptors = () => {
    interceptorsRequestIds.forEach((id) => hcAxios.interceptors.request.eject(id))
    interceptorsResponseIds.forEach((id) => hcAxios.interceptors.response.eject(id))
  }

  /**
   * 配置服务端环境
   */
  const setupServerEnvironment = () => {
    hcAxios.defaults.baseURL = baseURLServer
    // @ts-ignore
    hcAxios.defaults.headers.common.Referer = baseURLServerReferer

    interceptorsRequestIds.push(
      // @ts-ignore
      hcAxios.interceptors.request.use(handleDefaultServerQueries({ lang }))
    )
  }

  /**
   * 配置客户端环境
   */
  const setupClientEnvironment = () => {
    const dynamicURL = localCache.getCache('endpoint')?.baseURL
    hcAxios.defaults.baseURL = dynamicURL ? `https://${dynamicURL}` : baseURLClient

    interceptorsRequestIds.push(
      // @ts-ignore
      hcAxios.interceptors.request.use(handleSignature, (err) => Promise.reject(err))
    )

    interceptorsRequestIds.push(
      // @ts-ignore
      hcAxios.interceptors.request.use(handleDefaultClientQueries({ lang, platform, client }))
    )
  }

  /**
   * 添加响应拦截器
   */
  const setupResponseInterceptors = () => {
    // 处理业务逻辑错误
    interceptorsResponseIds.push(
      hcAxios.interceptors.response.use((response) => {
        const shouldHandleError =
          !response?.config.options?.disableErrorCodeResponseHandler && response?.data.code !== 200

        if (shouldHandleError) {
          if (response?.data.code === 401) {
            dispatchLogout()
          }
          if (response?.data.code === 10403) {
            // 使用正则匹配 /任意语言/error 或 /任意语言/error/
            const errorPathRegex = /^\/[a-z]{2,3}_[A-Z]{2}\/error\/?$/
            if (!errorPathRegex.test(location.pathname)) {
              location.href = '/error'
            }
          }
          errorNotifier.notify(response?.data.msg)
          return Promise.reject(response)
        }
        return response
      }, axiosResponseErrorHandler)
    )

    // 处理响应数据解密
    interceptorsResponseIds.push(hcAxios.interceptors.response.use(handleDecryptData))
  }

  // 执行初始化流程
  clearInterceptors()

  if (typeof window === 'undefined') {
    setupServerEnvironment()
  } else {
    setupClientEnvironment()
  }

  setupResponseInterceptors()
}

export default useAxiosInitializer
